/** Same rules as web/backend/src/utils/inspectionLeadTimes.js inspectionDueDate. */

function ymd(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseAnchorDate(anniversary) {
  if (anniversary instanceof Date && !Number.isNaN(anniversary.getTime())) {
    return new Date(anniversary.getFullYear(), anniversary.getMonth(), anniversary.getDate());
  }
  const s = String(anniversary || '').trim();
  if (!s) return null;
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function normalizeFrequency(freq) {
  return String(freq || 'ANNUAL').toUpperCase().replace(/-/g, '_');
}

function isAnnual(frequency) {
  const f = normalizeFrequency(frequency);
  return f === 'ANNUAL' || (f.includes('ANNUAL') && !f.includes('MONTHLY') && !f.includes('SIX'));
}

function frequencyStep(frequency) {
  const f = normalizeFrequency(frequency);
  if (f.includes('QUARTER')) return { step: 3, count: 4, tag: 'Q' };
  if (f.includes('SIX')) return { step: 6, count: 2, tag: 'H' };
  if (f === 'MONTHLY' || (f.includes('MONTH') && !f.includes('ANNUAL') && !f.includes('SIX'))) {
    return { step: 1, count: 12, tag: 'M' };
  }
  return { step: 12, count: 1, tag: 'Y' };
}

function addMonthsKeepDay(date, n) {
  const day = date.getDate();
  const rolled = new Date(date.getFullYear(), date.getMonth() + n, 1);
  const last = new Date(rolled.getFullYear(), rolled.getMonth() + 1, 0).getDate();
  rolled.setDate(Math.min(day, last));
  return rolled;
}

export function inspectionDueDate(anniversary, frequency, today = new Date()) {
  const anchor = parseAnchorDate(anniversary);
  if (!anchor) return null;
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const meta = frequencyStep(frequency);
  const shown = new Date(now.getFullYear(), anchor.getMonth(), anchor.getDate());
  let start = new Date(shown.getTime());
  if (start > now) start.setFullYear(start.getFullYear() - 1);
  let current = 1;
  let periodDue = addMonthsKeepDay(start, meta.step);
  for (let i = 1; i <= meta.count; i += 1) {
    const begin = addMonthsKeepDay(start, (i - 1) * meta.step);
    const due = addMonthsKeepDay(start, i * meta.step);
    if (now >= begin && now <= due) {
      current = i;
      periodDue = due;
      break;
    }
  }
  return {
    date: ymd(periodDue),
    term: meta.tag === 'Y' ? 'Y' : `${meta.tag}${current}`,
    periodDue: ymd(periodDue),
  };
}

export function anniversaryForInspection(row) {
  if (!row) return null;
  const freq = row.ss_frequency || row.inspection_frequency || row.bsi_frequency || row.sc_frequency || 'ANNUAL';
  const ss = row.ss_anniversary || row.compliance_schedule_anniversary || null;
  const building = row.building_anniversary || null;
  if (isAnnual(freq)) return building || ss || null;
  return ss || building || null;
}

export function applyInspectionDue(row, today = new Date()) {
  if (!row || typeof row !== 'object') return row;
  const freq = row.ss_frequency || row.inspection_frequency || row.bsi_frequency || row.sc_frequency || 'ANNUAL';
  const due = inspectionDueDate(anniversaryForInspection({ ...row, inspection_frequency: freq }), freq, today);
  if (!due) return { ...row, period_term: null, period_due: null };
  return { ...row, due_date: due.date, period_term: due.term, period_due: due.periodDue };
}

/** True when this period already has an issued visit result (pass, fail, or report). */
export function periodAlreadyInspected(row) {
  const type = String(row?.paperwork_type || '').toUpperCase();
  return (
    type === 'FORM_12A' ||
    type === 'S_RAD' ||
    type === 'DEFECT_NOTICE' ||
    !!row?.inspection_report_record_id ||
    !!row?.service_report_record_id
  );
}

/**
 * After a visit is done for the open period, the next due is the following period
 * (e.g. Q3 done → Q4 due, not the same Q3/Q4 end date again).
 */
export function nextPeriodInspectionDue(anniversary, frequency, today = new Date()) {
  const current = inspectionDueDate(anniversary, frequency, today);
  if (!current?.date) return null;
  const due = parseAnchorDate(current.date);
  if (!due) return null;
  const dayAfterDue = new Date(due.getFullYear(), due.getMonth(), due.getDate() + 1);
  return inspectionDueDate(anniversary, frequency, dayAfterDue);
}

/** Due to show on paperwork cards: advance when this period is already inspected. */
export function paperworkInspectionDue(row, today = new Date()) {
  const freq = row?.ss_frequency || row?.inspection_frequency || 'ANNUAL';
  const anchor = anniversaryForInspection({ ...row, inspection_frequency: freq });
  if (!anchor) return null;
  if (periodAlreadyInspected(row)) {
    return nextPeriodInspectionDue(anchor, freq, today);
  }
  return inspectionDueDate(anchor, freq, today);
}
